import { create } from 'zustand';
import type { Position as ApiPosition } from '../../generated-src/client/positions/api';
import { PositionStatusEnum } from '../../generated-src/client/positions/api';
import { apiService } from '../services/apiService';
import { getHttpStatus } from '../api/authErrorHandling';

export type FilterTab = 'all' | 'my';

export type MainTabId = 'vacancy' | 'search' | 'mailings' | 'dialogs' | 'scheduled-interviews' | 'analytics' | 'ats';

interface VacancyState {
  selectedVacancy: ApiPosition | null;
  selectedVacancyId: number | null;
  selectedVacancyLoading: boolean;
  selectedVacancyError: string | null;

  vacancies: ApiPosition[];

  filterTab: FilterTab;
  selectedStatusFilter: PositionStatusEnum | 'all';
  searchValue: string;

  currentPage: number;
  totalPages: number;

  statusDropdownOpen: boolean;
  isFirstLoad: boolean;

  lastActiveTabId: MainTabId | null;

  setSelectedVacancy: (vacancy: ApiPosition | null) => void;
  setSelectedVacancyById: (id: number | null) => void;
  clearSelectedVacancy: () => void;
  resetForAuthUserChange: () => void;
  refreshSelectedVacancy: () => Promise<void>;
  setVacancies: (vacancies: ApiPosition[]) => void;
  setFilterTab: (tab: FilterTab) => void;
  setStatusFilter: (status: PositionStatusEnum | 'all') => void;
  setSearchValue: (value: string) => void;
  setCurrentPage: (page: number) => void;
  setTotalPages: (pages: number) => void;
  toggleStatusDropdown: () => void;
  setStatusDropdownOpen: (open: boolean) => void;

  setLastActiveTabId: (tabId: MainTabId | null) => void;

  hideMainTabs: boolean;
  setHideMainTabs: (hide: boolean) => void;

  reset: () => void;
}

const SELECTED_VACANCY_ID_KEY = 'aiRecruiter_selectedVacancyId';
const LAST_ACTIVE_TAB_ID_KEY = 'aiRecruiter_lastActiveTabId';

type StoredAuthUser = {
  id?: number | string | null;
  email?: string | null;
  companyId?: number | string | null;
};

const normalizeScopePart = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim().toLowerCase();
  return normalized || null;
};

const readStoredAuthUser = (storage: Storage): StoredAuthUser | null => {
  const raw = storage.getItem('crm_auth_user');
  if (!raw) return null;

  const parsed = JSON.parse(raw) as StoredAuthUser;
  return parsed && (parsed.id != null || parsed.email) ? parsed : null;
};

const getStoredAuthUser = (): StoredAuthUser | null => {
  try {
    const localUser = readStoredAuthUser(localStorage);
    const sessionUser = readStoredAuthUser(sessionStorage);

    return sessionUser || localUser;
  } catch (e) {
    console.error('Error reading auth user for vacancy cache:', e);
  }
  return null;
};

const getVacancyCacheScope = (): string | null => {
  const user = getStoredAuthUser();
  if (!user) return null;

  const userPart = normalizeScopePart(user.id) || normalizeScopePart(user.email);
  if (!userPart) return null;

  const companyPart = normalizeScopePart(user.companyId) || 'no-company';
  return `company:${companyPart}:user:${userPart}`;
};

const getScopedStorageKey = (baseKey: string): string | null => {
  const scope = getVacancyCacheScope();
  return scope ? `${baseKey}:${scope}` : null;
};

const clearLegacyVacancyKeys = () => {
  try {
    localStorage.removeItem(SELECTED_VACANCY_ID_KEY);
    localStorage.removeItem(LAST_ACTIVE_TAB_ID_KEY);
    sessionStorage.removeItem(SELECTED_VACANCY_ID_KEY);
    sessionStorage.removeItem(LAST_ACTIVE_TAB_ID_KEY);
  } catch (e) {
    console.error('Error clearing legacy vacancy cache keys:', e);
  }
};

const isVacancyUnavailableStatus = (status: number | null): boolean => status === 403 || status === 404;

const getVacancyUnavailableError = (status: number | null): string =>
  status === 403 ? 'Нет доступа к выбранной вакансии' : 'Выбранная вакансия недоступна';

// Восстановление selectedVacancyId из localStorage, scoped by текущий пользователь/компания.
const getSavedVacancyId = (): number | null => {
  try {
    const key = getScopedStorageKey(SELECTED_VACANCY_ID_KEY);
    if (!key) return null;

    const saved = localStorage.getItem(key);
    if (!saved) return null;

    const parsed = parseInt(saved, 10);
    if (!Number.isSafeInteger(parsed) || parsed <= 0) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch (e) {
    console.error('Error reading selectedVacancyId from localStorage:', e);
    return null;
  }
};

const saveVacancyId = (id: number | null) => {
  try {
    clearLegacyVacancyKeys();
    const key = getScopedStorageKey(SELECTED_VACANCY_ID_KEY);
    if (!key) return;

    if (id !== null) {
      localStorage.setItem(key, String(id));
    } else {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.error('Error saving selectedVacancyId to localStorage:', e);
  }
};

const getSavedLastActiveTabId = (): MainTabId | null => {
  try {
    const key = getScopedStorageKey(LAST_ACTIVE_TAB_ID_KEY);
    if (!key) return null;

    const saved = localStorage.getItem(key);
    if (!saved) return null;
    const allowed: MainTabId[] = ['vacancy', 'search', 'mailings', 'dialogs', 'scheduled-interviews', 'analytics', 'ats'];
    return allowed.includes(saved as MainTabId) ? (saved as MainTabId) : null;
  } catch (e) {
    console.error('Error reading lastActiveTabId from localStorage:', e);
    return null;
  }
};

const saveLastActiveTabId = (tabId: MainTabId | null) => {
  try {
    clearLegacyVacancyKeys();
    const key = getScopedStorageKey(LAST_ACTIVE_TAB_ID_KEY);
    if (!key) return;

    if (tabId !== null) {
      localStorage.setItem(key, tabId);
    } else {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.error('Error saving lastActiveTabId to localStorage:', e);
  }
};

const createInitialState = () => ({
  selectedVacancy: null,
  selectedVacancyId: getSavedVacancyId(),
  selectedVacancyLoading: false,
  selectedVacancyError: null as string | null,
  vacancies: [],
  filterTab: 'all' as FilterTab,
  selectedStatusFilter: 'all' as PositionStatusEnum | 'all',
  searchValue: '',
  currentPage: 1,
  totalPages: 1,
  statusDropdownOpen: false,
  isFirstLoad: true,
  lastActiveTabId: getSavedLastActiveTabId(),
  hideMainTabs: false,
});

const initialState = createInitialState();

let selectedVacancyRequestSeq = 0;

export const useVacancyStore = create<VacancyState>((set, get) => ({
  ...initialState,

  setSelectedVacancy: (vacancy) => {
    console.log('VacancyStore: Setting selected vacancy:', vacancy?.title);
    selectedVacancyRequestSeq += 1;
    const vacancyId = vacancy?.id ?? null;
    saveVacancyId(vacancyId); // Сохраняем в localStorage
    set({
      selectedVacancy: vacancy,
      selectedVacancyId: vacancyId,
      selectedVacancyLoading: false,
      selectedVacancyError: null,
    });
  },

  setSelectedVacancyById: (id) => {
    const { vacancies, selectedVacancy } = get();
    const vacancy = vacancies.find(v => v.id === id);
    const requestSeq = ++selectedVacancyRequestSeq;

    console.log('VacancyStore: Setting selected vacancy by ID:', id, 'Found in list:', !!vacancy, 'Title:', vacancy?.title);
    console.log('VacancyStore: Current selectedVacancy:', selectedVacancy?.title, 'ID:', selectedVacancy?.id);

    saveVacancyId(id);

    if (id === null) {
      get().clearSelectedVacancy();
      return;
    }

    if (vacancy) {
      console.log('VacancyStore: Setting ID and preview vacancy data');
      set({
        selectedVacancy: selectedVacancy?.id === id ? { ...vacancy, ...selectedVacancy } : vacancy,
        selectedVacancyId: id,
        selectedVacancyLoading: true,
        selectedVacancyError: null,
      });
    } else {
      const shouldKeepVacancy = selectedVacancy?.id === id;
      console.log('VacancyStore: Vacancy not in list. Setting ID only. Keep current vacancy?', shouldKeepVacancy);
      set({
        selectedVacancyId: id,
        selectedVacancy: shouldKeepVacancy ? selectedVacancy : null,
        selectedVacancyLoading: true,
        selectedVacancyError: null,
      });
      console.log('VacancyStore: After set - selectedVacancy:', get().selectedVacancy?.title);
    }

    void apiService.getPosition(id)
      .then((freshVacancy) => {
        if (selectedVacancyRequestSeq !== requestSeq || get().selectedVacancyId !== id) {
          return;
        }

        set({
          selectedVacancy: freshVacancy,
          selectedVacancyLoading: false,
          selectedVacancyError: null,
          vacancies: get().vacancies.map(v => (
            v.id === id ? { ...v, ...freshVacancy } : v
          )),
        });
      })
      .catch((error) => {
        if (selectedVacancyRequestSeq !== requestSeq || get().selectedVacancyId !== id) {
          return;
        }
        console.error('VacancyStore: Failed to load selected vacancy:', error);
        const status = getHttpStatus(error);
        if (isVacancyUnavailableStatus(status)) {
          get().clearSelectedVacancy();
          set({ selectedVacancyError: getVacancyUnavailableError(status) });
          return;
        }
        set({
          selectedVacancyLoading: false,
          selectedVacancyError: error instanceof Error ? error.message : 'Failed to load selected vacancy',
        });
      });
  },

  clearSelectedVacancy: () => {
    selectedVacancyRequestSeq += 1;
    saveVacancyId(null);
    set({
      selectedVacancyId: null,
      selectedVacancy: null,
      selectedVacancyLoading: false,
      selectedVacancyError: null,
    });
  },

  resetForAuthUserChange: () => {
    selectedVacancyRequestSeq += 1;
    set(createInitialState());
  },

  refreshSelectedVacancy: async () => {
    const { selectedVacancyId } = get();
    if (!selectedVacancyId) {
      console.log('VacancyStore: No vacancy selected to refresh');
      return;
    }

    const requestSeq = ++selectedVacancyRequestSeq;
    set({ selectedVacancyLoading: true, selectedVacancyError: null });

    try {
      console.log('VacancyStore: Refreshing vacancy data from server, ID:', selectedVacancyId);
      const freshVacancy = await apiService.getPosition(selectedVacancyId);
      if (selectedVacancyRequestSeq !== requestSeq || get().selectedVacancyId !== selectedVacancyId) {
        return;
      }
      console.log('VacancyStore: Got fresh vacancy data:', freshVacancy?.title);
      
      set({
        selectedVacancy: freshVacancy,
        selectedVacancyLoading: false,
        selectedVacancyError: null,
      });
      
      const { vacancies } = get();
      const updatedVacancies = vacancies.map(v => 
        v.id === selectedVacancyId ? { ...v, ...freshVacancy } : v
      );
      set({ vacancies: updatedVacancies });
      
      console.log('VacancyStore: Vacancy data refreshed successfully');
    } catch (error) {
      if (selectedVacancyRequestSeq !== requestSeq || get().selectedVacancyId !== selectedVacancyId) {
        return;
      }
      console.error('VacancyStore: Failed to refresh vacancy:', error);
      const status = getHttpStatus(error);
      if (isVacancyUnavailableStatus(status)) {
        get().clearSelectedVacancy();
        set({ selectedVacancyError: getVacancyUnavailableError(status) });
        return;
      }
      set({
        selectedVacancyLoading: false,
        selectedVacancyError: error instanceof Error ? error.message : 'Failed to refresh selected vacancy',
      });
    }
  },

  setVacancies: (vacancies) => {
    set({ vacancies });

    // Если была выбрана вакансия, обновляем её данные (но НЕ сбрасываем если не нашли)
    const { selectedVacancyId, selectedVacancy } = get();
    if (selectedVacancyId) {
      const updatedVacancy = vacancies.find(v => v.id === selectedVacancyId);
      if (updatedVacancy) {
        // Вакансия найдена в новом списке - обновляем превью, не теряя поля полной вакансии
        set({
          selectedVacancy: selectedVacancy
            ? { ...selectedVacancy, ...updatedVacancy }
            : updatedVacancy
        });
      }
      // Если не нашли, оставляем selectedVacancy как есть (может быть отфильтрована)
    }
  },

  setFilterTab: (tab) => {
    set({ filterTab: tab, currentPage: 1 });
  },

  setStatusFilter: (status) => {
    set({ selectedStatusFilter: status, currentPage: 1, statusDropdownOpen: false });
  },

  setSearchValue: (value) => {
    set({ searchValue: value, currentPage: 1 });
  },

  setCurrentPage: (page) => {
    set({ currentPage: page });
  },

  setTotalPages: (pages) => {
    set({ totalPages: pages });
  },

  toggleStatusDropdown: () => {
    set((state) => ({ statusDropdownOpen: !state.statusDropdownOpen }));
  },

  setStatusDropdownOpen: (open) => {
    set({ statusDropdownOpen: open });
  },

  setLastActiveTabId: (tabId) => {
    saveLastActiveTabId(tabId);
    set({ lastActiveTabId: tabId });
  },

  hideMainTabs: false,
  setHideMainTabs: (hide: boolean) => set({ hideMainTabs: hide }),

  reset: () => {
    selectedVacancyRequestSeq += 1;
    set(createInitialState());
  },
}));
